import { DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActionSheetController, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PlatformEnum } from '@panamax/app-state';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { IssueDetails, IssueOptions, RecoveryRequest } from '@usf/list-types';
import { BehaviorSubject } from 'rxjs';
import { Button } from '@shared/models/ellipsis-option.model';

@Component({
  selector: 'app-recovery-order-modal',
  templateUrl: './recovery-order-modal.component.html',
  styleUrls: ['./recovery-order-modal.component.scss'],
})
export class RecoveryOrderModalComponent implements OnInit {
  @ViewChild('emailInfoPopover') popover;

  @Input() platform: PlatformEnum;
  @Input() orderNumber: number;
  @Input() showEmail = false;
  @Input() issueOptions!: IssueOptions[];

  @Output() dismissEvent: EventEmitter<void> = new EventEmitter();
  @Output() submitEvent: EventEmitter<RecoveryRequest> = new EventEmitter();

  readonly platformEnum = PlatformEnum;

  blockSubmission: BehaviorSubject<boolean> = new BehaviorSubject(false);
  openPopover = false;
  timeoutFunc;
  comments: string;
  email?: string;
  currentDate = new Date();
  tomorrow = new Date();
  dateString: string;
  dateButtons: any[];
  recoveryReasonButtons: Button[];
  recoveryReason: IssueDetails;

  constructor(
    private popoverController: PopoverController,
    private translateService: TranslateService,
    private datePipe: DatePipe,
    private actionSheetController: ActionSheetController,
  ) {}

  ngOnInit() {
    this.setSubmitButtonReady();
    this.tomorrow.setDate(this.currentDate.getDate() + 1);
    this.loadRecoveryReasonsButton(this.issueOptions);
    this.dateString =
      this.translateService.instant(
        'i18n.orderSubmittedPage.recoveryModal.today',
      ) +
      ' (' +
      this.datePipe.transform(this.currentDate, 'M/d/YYYY') +
      ')';
    this.dateButtons = [
      {
        text:
          this.translateService.instant(
            'i18n.orderSubmittedPage.recoveryModal.today',
          ) +
          ' (' +
          this.datePipe.transform(this.currentDate, 'M/d/YYYY') +
          ')',
        handler: () => {
          (this.dateString =
            this.translateService.instant(
              'i18n.orderSubmittedPage.recoveryModal.today',
            ) +
            ' (' +
            this.datePipe.transform(this.currentDate, 'M/d/YYYY') +
            ')'),
            this.dismissBottomHalfPopups();
        },
      },
      {
        text:
          this.translateService.instant(
            'i18n.orderSubmittedPage.recoveryModal.tomorrow',
          ) +
          ' (' +
          this.datePipe.transform(this.tomorrow, 'M/d/YYYY') +
          ')',
        handler: () => {
          (this.dateString =
            this.translateService.instant(
              'i18n.orderSubmittedPage.recoveryModal.tomorrow',
            ) +
            ' (' +
            this.datePipe.transform(this.tomorrow, 'M/d/YYYY') +
            ')'),
            this.dismissBottomHalfPopups();
        },
      },
    ];
  }

  dismiss() {
    this.dismissEvent.emit();
  }

  async openRecoveryPopover(event: any, buttons: any[]) {
    const listOptionsPopover = await this.popoverController.create({
      component: EllipsisOptionsPopoverComponent,
      event,
      componentProps: {
        buttons,
      },
      cssClass: 'recovery-order-list-options-popover-desktop',
      mode: 'ios',
      showBackdrop: false,
      side: 'bottom',
      alignment: 'center',
    });
    await listOptionsPopover.present();
  }

  async openActionSheet(buttons: any[]) {
    buttons.push({
      text: this.translateService.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
    });
    const actionSheet = await this.actionSheetController.create({
      cssClass: 'action-sheet-touch',
      mode: 'ios',
      buttons,
    });
    await actionSheet.present();
  }

  dismissBottomHalfPopups() {
    if (this.platform === this.platformEnum.desktop) {
      this.popoverController.dismiss();
    } else {
      this.actionSheetController.dismiss();
    }
  }

  onSubmit() {
    const recoveryRequest = {
      desc: this.comments,
      recvryRsn: this.recoveryReason.reasonCode,
      reqDelvryDt: this.dateString,
    } as RecoveryRequest;

    if (this.showEmail) {
      if (this.email) {
        recoveryRequest.webEmail = this.email;
        this.submitEvent.emit(recoveryRequest);
      }
      return;
    }

    this.submitEvent.emit(recoveryRequest);
  }

  popoverInteraction = (event: any, interact: boolean) => {
    event.stopPropagation();
    if (event !== undefined) {
      this.popover.event = event;
      this.timeoutFunc = setTimeout(() => {
        this.openPopover = false;
      }, 5000);
    }
    this.openPopover = interact;
  };

  setSubmitButtonReady() {
    let blockSubmit = this.showEmail && !this.email;
    this.blockSubmission.next(blockSubmit);
  }

  // there is no option in the ui for issueDetails so we are going to user the first one
  loadRecoveryReasonsButton(issueOptions: IssueOptions[]) {
    this.recoveryReasonButtons = issueOptions
      .filter(issue => issue.issueDetails && issue.issueDetails.length > 0)
      .map((issue: IssueOptions) => {
        return {
          text: issue.issueDetails[0].displayText,
          handler: () => {
            this.recoveryReason = issue.issueDetails[0];
            this.dismissBottomHalfPopups();
          },
        };
      });
    this.recoveryReason = this.issueOptions[0].issueDetails[0];
  }
}
